import {
  Droplets,
  Sun,
  Leaf,
  Fence,
  TreePine,
  Home as HomeIcon,
  Mountain,
  CloudRain,
  Grid2X2,
  Building2,
} from "lucide-react";

/**
 * Single source of truth for every service Wynridge offers.
 * Each entry powers:
 *   - the service card on the landing page
 *   - the standalone /services/:slug detail page
 */
export const services = [
  {
    slug: "house-wash",
    seoSlug: "house-washing-goodlettsville-tn",
    seoTitle: "House Washing & Soft Washing",
    icon: HomeIcon,
    title: "House Wash",
    priceFrom: 279,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/house-wash-stock-after.jpg",
    shortDesc:
      "Gentle soft-wash treatment safe for vinyl, brick, stucco, and painted siding — lifts mildew, pollen, and grime without harming finishes.",
    hero:
      "A full-home soft wash that restores curb appeal in a single visit. Low-pressure, high-detergent, high-attention.",
    longDesc:
      "Traditional pressure washing can strip paint, drive water behind siding, and etch soft materials. Our house wash uses a low-pressure soft-wash system with a professional-grade surfactant blend that clings to the surface, dwells long enough to break down organic growth, and rinses away clean. Two-story homes, textured stucco, delicate painted trim — all handled without lifting a single seam. Plant-safe eco-friendly detergents are available on request at no extra charge.",
    includes: [
      "Whole-home soft-wash rinse with professional-grade detergent",
      "Foundation-to-eave application on every accessible side",
      "Downspout, gutter face, and soffit exterior cleaned",
      "Window exteriors rinsed of runoff",
      "Landscape pre-soak so plants stay safe",
      "Full cleanup — hoses coiled, walkways rinsed",
    ],
    process: [
      { step: "Walk & protect", detail: "We meet you, walk the home, and pre-soak greenery and delicate landscaping." },
      { step: "Apply", detail: "Biodegradable soft-wash solution is applied bottom-up so the runoff never streaks." },
      { step: "Dwell", detail: "5–10 minutes of dwell time lets the detergent break down mildew, algae, and pollen." },
      { step: "Rinse & inspect", detail: "Low-pressure rinse from top down. We walk the property with you before we leave." },
    ],
    idealFor: [
      "Homes with visible mildew stripes or green streaks",
      "Pre-listing prep before a real-estate photo shoot",
      "Annual spring cleaning after pollen season",
      "HOA compliance visits",
    ],
  },
  {
    slug: "driveway",
    seoSlug: "driveway-cleaning-goodlettsville-tn",
    seoTitle: "Driveway & Concrete Cleaning",
    icon: Droplets,
    title: "Driveway",
    priceFrom: 179,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/driveway-goodlettsville-after.webp",
    shortDesc:
      "Surface-cleaner pass on concrete, pavers, or stamped concrete. Oil stains, mildew, and tire marks lifted away.",
    hero:
      "The single biggest curb-appeal win for most homes. We take your driveway from grey-and-grimy back to the day it was poured.",
    longDesc:
      "Concrete is porous — oil, tire rubber, mildew, and rust don't just sit on top, they soak in. We use a dedicated 20\" surface-cleaner tool that maintains an even 3,500-PSI rotating spray, so you get uniform results with no zebra-striping. Oil spots get pre-treated with a degreaser, rust spots get an oxalic pass, and the whole slab is rinsed and left spotless.",
    includes: [
      "Full-drive surface-clean at consistent pressure",
      "Oil-spot pre-treatment with commercial degreaser",
      "Rust and mildew targeted treatment as needed",
      "Perimeter edges hand-detailed",
      "Post-clean rinse of gutters and street apron",
    ],
    process: [
      { step: "Inspect", detail: "We identify oil, rust, and problem spots up front so nothing gets missed." },
      { step: "Pre-treat", detail: "Targeted degreaser and rust remover applied to stains and given time to work." },
      { step: "Surface clean", detail: "Full-driveway pass with a rotating surface-cleaner for uniform, streak-free finish." },
      { step: "Detail & rinse", detail: "Hand-detail the perimeter and any spots the surface tool couldn't reach." },
    ],
    idealFor: [
      "Homes with visible oil, rust, or tire stains",
      "Pre-sale curb appeal boost",
      "Post-construction concrete cleanup",
      "Sealing prep — a clean surface holds sealer far longer",
    ],
  },
  {
    slug: "roof-wash",
    seoSlug: "roof-cleaning-goodlettsville-tn",
    seoTitle: "Roof Cleaning & Soft Wash",
    icon: Mountain,
    title: "Roof Wash",
    priceFrom: 499,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/roof-wash-stock-after.jpg",
    shortDesc:
      "Low-pressure soft-wash with commercial-grade detergents — kills the black streaks (gloeocapsa magma) without damaging shingles.",
    hero:
      "The black streaks aren't dirt — they're living algae eating your shingles. We kill them safely, and they stay gone for years.",
    longDesc:
      "Those black streaks on your roof are a cyanobacterium called gloeocapsa magma. Pressure washing a roof will void your shingle warranty and can dislodge granules. Our roof soft-wash uses a low-pressure application of sodium hypochlorite blended with a surfactant — it kills the algae on contact and rinses clean. Results are typically visible within days, and full brightness returns within a week or two of the first rain.",
    includes: [
      "Full soft-wash application on every roof plane",
      "Gutter faces and downspouts rinsed",
      "Landscape pre-soak protection",
      "Post-wash walk-through to verify coverage",
      "Written recommendation for next re-treatment (usually 3–5 years)",
    ],
    process: [
      { step: "Assess", detail: "We evaluate roof pitch, material, and access to plan the safest approach." },
      { step: "Protect", detail: "All surrounding plants pre-soaked; downspout exits covered as needed." },
      { step: "Apply", detail: "Low-pressure soft-wash solution applied from a ground-mounted or ladder-based platform." },
      { step: "Verify", detail: "Every plane inspected before we leave. Full brightening happens over the following 1–2 weeks." },
    ],
    idealFor: [
      "Asphalt shingle roofs with visible black streaks",
      "Homes preparing to sell (roof stains hurt listings hard)",
      "HOA compliance letters citing roof appearance",
      "Preventive care every 3–5 years",
    ],
  },
  {
    slug: "gutter-cleaning",
    seoSlug: "gutter-cleaning-goodlettsville-tn",
    seoTitle: "Gutter Cleaning & Brightening",
    icon: CloudRain,
    title: "Gutter Cleaning",
    priceFrom: 139,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/gutters-goodlettsville-after.jpg",
    shortDesc:
      "Clear leaves, sticks, and grit so your downspouts do their real job in the next storm.",
    hero:
      "Clogged gutters cause every expensive water problem a home can have. We flush them clean — inside and out.",
    longDesc:
      "A single clogged downspout can dump hundreds of gallons of water onto your foundation in one storm. We hand-remove all debris from your gutters, flush every downspout with a hose to confirm flow, bag everything neatly, and leave your roof line looking sharp. This is one of the cheapest preventive-maintenance visits a homeowner can make.",
    includes: [
      "Hand-removal of all gutter debris",
      "Downspout flush and flow verification",
      "Debris bagged and hauled away",
      "Visual inspection for loose hangers or sagging sections",
      "Report of any repairs we notice while up top",
    ],
    process: [
      { step: "Setup", detail: "Ladders staged on stable footing; drop-cloths under key sections." },
      { step: "Clear", detail: "Hand-remove debris one section at a time — nothing gets pushed to the downspouts." },
      { step: "Flush", detail: "Every downspout hosed until we confirm free flow at the exit." },
      { step: "Report", detail: "We photograph anything that needs attention and email the summary." },
    ],
    idealFor: [
      "Homes with mature trees overhanging the roof",
      "Fall and spring seasonal maintenance",
      "Anyone who's seen water spilling over the edge during a storm",
      "Pre-winter prep to prevent ice damming",
    ],
  },
  {
    slug: "windows",
    seoSlug: "window-cleaning-goodlettsville-tn",
    seoTitle: "Window Cleaning — Streak-Free Glass",
    icon: Grid2X2,
    title: "Windows",
    priceFrom: 139,
    priceLabel: "Exterior from",
    priceDetail: "$189 interior + exterior",
    afterPhoto: "/portfolio/window-cleaning-stock-after.jpg",
    shortDesc:
      "Crystal-clear windows inside, outside, or both — no streaks, no drips, no lint.",
    hero:
      "The single fastest way to make a home feel brand-new. Streak-free glass, spotless frames, professional finish.",
    longDesc:
      "We hand-clean every pane with a professional squeegee-and-scrim technique — no lazy spray bottles, no paper towels, no film. Frames, sills, and tracks are wiped as part of the service. Choose exterior-only for the fastest turnaround, or interior + exterior for a full-home refresh. Screens can be rinsed and re-installed on request.",
    includes: [
      "Every accessible glass pane hand-cleaned to streak-free finish",
      "Frame and sill wipe-down (accessible portions)",
      "Sill and track vacuum (interior packages)",
      "Screen rinse on request",
      "Second-story exteriors handled with water-fed pole system",
    ],
    process: [
      { step: "Walk", detail: "We count panes, note any special glass (leaded, tinted, film), and confirm scope." },
      { step: "Clean", detail: "Squeegee-and-scrim on every pane. Detergent solution matched to glass condition." },
      { step: "Detail", detail: "Frames, sills, and tracks wiped or vacuumed depending on package." },
      { step: "Inspect", detail: "Every window checked at eye level from inside before we call it done." },
    ],
    idealFor: [
      "Post-construction cleanup",
      "Pre-listing photography prep",
      "Twice-yearly maintenance in pollen and storm-heavy regions",
      "Anyone whose windows haven't been touched in more than a year",
    ],
  },
  {
    slug: "sidewalk",
    icon: Sun,
    title: "Sidewalk",
    priceFrom: 99,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/sidewalk-stock-after.jpg",
    shortDesc:
      "Spotless walkways and front-porch approaches that lift the whole curb-appeal of your home.",
    hero:
      "The first fifteen feet a guest walks. We make sure those fifteen feet look great.",
    longDesc:
      "Sidewalks and walkways collect algae, mildew, dropped soil, and shoe grime faster than any other surface on your property. A single surface-clean pass takes them back to the color the concrete was actually poured in. Great as a stand-alone visit or paired with driveway service.",
    includes: [
      "Uniform surface-clean along the full walkway",
      "Perimeter edges detailed by hand",
      "Front-porch approach and stoop included where accessible",
      "Post-clean rinse of adjacent landscaping",
    ],
    process: [
      { step: "Prep", detail: "Sweep off loose leaves and debris." },
      { step: "Pre-treat", detail: "Any heavy mildew spots get a detergent pre-soak." },
      { step: "Surface clean", detail: "Rotating cleaner pass gives an even, streak-free finish." },
      { step: "Detail", detail: "Hand-wand the joints, corners, and steps." },
    ],
    idealFor: [
      "Front walkways looking green or gray",
      "Any home where the sidewalk is more visible from the street than the driveway",
      "Pre-event refresh (parties, showings, family visits)",
    ],
  },
  {
    slug: "patio",
    icon: Leaf,
    title: "Patio",
    priceFrom: 129,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/patio-goodlettsville-after.webp",
    shortDesc:
      "Concrete, stone, or paver patios brought back to showroom-clean. Furniture moved as needed.",
    hero:
      "Your outdoor room, back to the way it looked when you first set it up. Ready for a Saturday evening.",
    longDesc:
      "Patios take the brunt of the weather. Pollen, spilled drinks, leaf tannins, and organic growth all settle into the surface pores. We move light furniture, surface-clean the whole patio, and hand-detail the tight spots grills and planters make impossible for a machine. Paver patios get a joint-safe treatment so we don't blow sand out of the seams.",
    includes: [
      "Furniture moved (light) and replaced when dry",
      "Surface-clean pass across the main patio field",
      "Joint-safe technique on pavers and flagstone",
      "Hand-detail around grills, planters, and railings",
      "Optional joint-sand refresh recommendation",
    ],
    process: [
      { step: "Reset", detail: "We move light furniture to the yard and note anything that stays put." },
      { step: "Pre-treat", detail: "Organic growth gets a detergent soak; leaf tannin stains get a targeted lift." },
      { step: "Wash", detail: "Surface-cleaner across the field; hand-wand for edges and corners." },
      { step: "Reset & rinse", detail: "Furniture back in place, walkway rinsed, everything ready to enjoy." },
    ],
    idealFor: [
      "Spring opening after a wet winter",
      "Pre-event refresh before hosting",
      "Rental turns where patios matter",
      "Anyone tired of tracking green in on their shoes",
    ],
  },
  {
    slug: "deck",
    icon: TreePine,
    title: "Deck",
    priceFrom: 199,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/deck-stock-after.jpg",
    shortDesc:
      "Right pressure, right detergents — wood, composite, and stone decks come back to life without lifting grain.",
    hero:
      "Wood decks are delicate. We know exactly which pressure and which detergent your material needs.",
    longDesc:
      "Deck cleaning is where amateur pressure washing does the most damage. Too much PSI on softwood raises the grain and shortens the life of the deck. We tailor the approach to your material — cedar, pine, treated lumber, composite, or stone — and use a low-pressure spray with a wood-safe detergent that lifts the gray, mildew, and pollen without lifting the grain. Great prep before a stain or sealer refresh.",
    includes: [
      "Material-appropriate pressure and detergent selection",
      "Full deck field, stairs, and handrail cleaning",
      "Detail work around posts, spindles, and joist edges",
      "Post-clean recommendation on sealing or staining if visible",
    ],
    process: [
      { step: "Identify", detail: "We check the material and condition — cedar, pine, treated, composite, or stone." },
      { step: "Detergent", detail: "Wood-safe brightener applied to lift gray oxidation without stripping fibers." },
      { step: "Clean", detail: "Low-pressure spray tuned to the material; no grain-raising, no fuzzy boards." },
      { step: "Advise", detail: "If a stain or seal refresh would help, we tell you what timeline to plan on." },
    ],
    idealFor: [
      "Decks that have grayed out over winter",
      "Prep before staining or sealing",
      "Pre-listing curb appeal for backyard living space",
      "Any deck that has never been professionally cleaned",
    ],
  },
  {
    slug: "fence",
    icon: Fence,
    title: "Fence",
    priceFrom: 219,
    priceLabel: "Starting at",
    priceDetail: null,
    afterPhoto: "/portfolio/fence-stock-after.jpg",
    shortDesc:
      "Cedar, pine, or treated lumber — a gentle wash that strips grime and prepares the wood for stain or sealant.",
    hero:
      "A clean fence lifts the whole yard. Ours come back with the color of the wood, not the gray of neglect.",
    longDesc:
      "Wood fences discolor from UV, mildew, and settled dust. We wash both sides at low pressure with a wood-appropriate detergent, giving you a clean surface either for its own sake or as prep for a stain or sealant. Metal and vinyl fencing use a different but equally gentle technique.",
    includes: [
      "Both sides of the fence cleaned where accessible",
      "Wood-safe detergent, tuned to your fence material",
      "Post-clean rinse of adjacent landscaping",
      "Stain-and-seal recommendation if we see fading",
    ],
    process: [
      { step: "Walk", detail: "We identify sections that need extra attention and any repair recommendations." },
      { step: "Pre-soak", detail: "Detergent applied and given dwell time to lift years of gray." },
      { step: "Wash", detail: "Low-pressure spray both sides; we don't blast fresh wood grain." },
      { step: "Rinse", detail: "Full rinse of the fence and any plants near the line." },
    ],
    idealFor: [
      "Fences prep for staining or sealing",
      "Any wood fence more than two years old",
      "HOA fence-condition compliance",
      "Anyone selling a home with a visible perimeter fence",
    ],
  },
  {
    slug: "commercial",
    seoSlug: "commercial-pressure-washing-goodlettsville-tn",
    seoTitle: "Commercial Pressure Washing",
    icon: Building2,
    title: "Commercial",
    priceFrom: 399,
    priceLabel: "Quoted per property",
    priceDetail: "Storefronts, HOAs, offices",
    afterPhoto: "/portfolio/commercial-stock-after.jpg",
    shortDesc:
      "Storefronts, HOAs, offices, apartments, and light-industrial facilities. Scheduled or after-hours service to keep your property client-ready.",
    hero:
      "Your storefront is your handshake. We keep it clean, safe, and professional — before your first customer walks in.",
    longDesc:
      "Commercial exterior cleaning has different demands than residential — insurance, safety compliance, scheduling around business hours, and consistency across multi-tenant properties. We handle storefronts, restaurant patios, gas-station canopies, drive-thru pads, dumpster corrals, HOA common areas, apartment sidewalks and entryways, medical-office exteriors, and light-industrial concrete pads. Recurring service contracts available with predictable per-visit pricing.",
    includes: [
      "Certificate of insurance provided on request",
      "After-hours and pre-open scheduling to avoid disruption",
      "Recurring service contracts (monthly / quarterly / seasonal)",
      "Consistent crew and process across visits",
      "Written scope and per-visit invoicing",
      "Compliance with common HOA and property-management specs",
    ],
    process: [
      { step: "Walk & scope", detail: "We visit the property, measure surfaces, and confirm access needs (water, power, gates)." },
      { step: "Written proposal", detail: "You get a per-visit price and a recurring option — no verbal-only quotes." },
      { step: "Schedule around you", detail: "We work early morning, late evening, or weekends so your business stays open." },
      { step: "Report & invoice", detail: "Every visit closes with a photo report and same-day invoice." },
    ],
    idealFor: [
      "Storefronts & retail entryways",
      "HOAs with common walkways, pool decks, or signage",
      "Restaurants (drive-thru pads, patios, dumpster corrals)",
      "Property managers with multiple properties",
      "Medical, dental, and professional office exteriors",
    ],
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug || s.seoSlug === slug);

// Six primary SEO services shown on the services hub and home page
export const CORE_SEO_SLUGS = [
  "house-washing-goodlettsville-tn",
  "roof-cleaning-goodlettsville-tn",
  "driveway-cleaning-goodlettsville-tn",
  "gutter-cleaning-goodlettsville-tn",
  "window-cleaning-goodlettsville-tn",
  "commercial-pressure-washing-goodlettsville-tn",
];

export const coreServices = () =>
  CORE_SEO_SLUGS.map((slug) => services.find((s) => s.seoSlug === slug)).filter(Boolean);

export const formatPrice = (s) => (s.priceDetail ? `$${s.priceFrom}` : `$${s.priceFrom}`);
